import { startOfTomorrow } from 'date-fns/startOfTomorrow';
import React, { useEffect, useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';

import { useConfig } from '../../../defaultConfig';
import { useFirstBankingDayDispatch } from '../../../fetchHooks/useBankingDays';
import usePaymentLimits from '../../../fetchHooks/usePaymentLimits';
import { usePaymentAccountContext } from '../../../hooks/useAccountState';
import useCurrency from '../../../hooks/useCurrency';
import useHandleCrossBorderReload from '../../../hooks/useHandleCrossBorderReload';
import useTranslation from '../../../hooks/useTranslation';
import { NoteTypes } from '../../../translations/translationType';
import { AmountInput, AmountBox } from '../../AmountBox';
import { NoteField } from '../../NoteField';
import { DateField } from '../../shared/DateField';
import { NavigationButtons } from '../../shared/NavigationButtons';
import { SelectList } from '../../shared/SelectList';
import BicField from './BicField';
import ChargeBearerField from './ChargeBearerField';
import CurrencyExchange from './CurrencyExchange';
import CurrencyField from './CurrencyField';
import PriorityField from './PriorityField';
import TransferCurrencyField from './TransferCurrencyField';
import { CrossBorderPaymentActionType } from './form/actions';
import { GB_COUNTRY_CODE, GB_CURRENCY } from './form/state';
import useFormContext from './form/useFormContext';
import {
    AmountFieldsError,
    isDomesticPayment,
    isNoCoverage,
    validateAmount,
    validateAmountFields,
} from './form/validate';

import './CrossBorderAmountFields.scss';

const today = getZonedDate();
const tomorrow = getZonedDate(startOfTomorrow());
const MAX_CREDITOR_MESSAGE = 140;

const CrossBorderAmountFields: React.FC = () => {
    const translated = useTranslation();
    const { state, handleChange, errors } = useFormContext();
    const { targetFromAccount } = usePaymentAccountContext();
    const { featureToggles } = useConfig();
    const [validationErrors, setValidationErrors] = useState<AmountFieldsError>();
    const [calculatedCurrencyExchange, setCalculatedCurrencyExchange] = useState<number>();
    const defaultCurrency = useCurrency(state);
    const notDefaultCountryCurrency = defaultCurrency !== state.country?.currency && state.receiver?.needBic;
    useHandleCrossBorderReload();

    useFirstBankingDayDispatch(tomorrow, handleChange, CrossBorderPaymentActionType.FIRST_BANKING_DATE_FROM_TODAY);

    const paymentLimits = usePaymentLimits();

    useEffect(() => {
        handleChange({
            type: CrossBorderPaymentActionType.SET_AMOUNT_IN_NOK,
            payload: calculatedCurrencyExchange,
        });
    }, [calculatedCurrencyExchange, handleChange]);

    useEffect(() => {
        if (defaultCurrency) {
            handleChange({
                type: CrossBorderPaymentActionType.CURRENCY,
                payload: defaultCurrency,
            });
        }
    }, [handleChange, defaultCurrency]);

    const showChargeBearer = state.country?.countryCode?.value === GB_COUNTRY_CODE && state.currency === GB_CURRENCY;

    const validateAmountFieldsMobile = () => {
        const validationErrors = validateAmountFields(
            translated,
            state,
            undefined,
            paymentLimits.data,
            targetFromAccount,
        );
        setValidationErrors(validationErrors);

        return !(
            validationErrors.amount ||
            validationErrors.amountLimit ||
            validationErrors.currency ||
            validationErrors.transferCurrency ||
            validationErrors.messageReceiver ||
            validationErrors.bic
        );
    };

    return (
        <>
            <h2 className="cross-border-payment-fields__title">{translated.crossBorderPayment.amountSubTitle}</h2>
            <AmountBox
                isNoCoverage={isNoCoverage(state, targetFromAccount)}
                limitWarning={validateAmount(translated, state, paymentLimits.data, targetFromAccount)?.amountLimit}
                domesticPaymentWarning={isDomesticPayment(state)}
                error={validationErrors?.amount ?? errors.amount}
                limitError={validationErrors?.amountLimit ?? errors.amountLimit}
            >
                <AmountInput
                    value={state.amount}
                    onChange={(value) =>
                        handleChange({
                            type: CrossBorderPaymentActionType.AMOUNT,
                            payload: value,
                        })
                    }
                    ariaLabel={translated.payAmount}
                    currency={state.currency ?? state.country?.currency ?? 'EUR'}
                />
            </AmountBox>
            <div className="cross-border-payment-exchange">
                <CurrencyExchange
                    selectedCurrency={state.currency ?? state.country?.currency ?? 'EUR'}
                    amount={state.amount ?? 0}
                    calculatedCurrencyExchange={(amount) => setCalculatedCurrencyExchange(amount)}
                />
            </div>
            <SelectList>
                <li>
                    <CurrencyField
                        label={translated.currencyField.label}
                        currency={state.currency}
                        onChange={(value) =>
                            handleChange({
                                type: CrossBorderPaymentActionType.CURRENCY,
                                payload: value,
                            })
                        }
                        onTransferCurrencyChange={(value) =>
                            handleChange({
                                type: CrossBorderPaymentActionType.TRANSFER_CURRENCY,
                                payload: value,
                            })
                        }
                        showKinaInfo={state.currency === 'CNY'}
                    />
                </li>
                {state.transferCurrency && (
                    <li>
                        <TransferCurrencyField
                            label={translated.currencyField.title}
                            transferCurrency={state.transferCurrency}
                            onChange={(value) =>
                                handleChange({
                                    type: CrossBorderPaymentActionType.TRANSFER_CURRENCY,
                                    payload: value,
                                })
                            }
                        />
                    </li>
                )}
                <li>
                    <DateField
                        label={translated.date}
                        minDate={today}
                        date={state.paymentDate ?? getZonedDate()}
                        onChange={(date?: Date) =>
                            handleChange({
                                type: CrossBorderPaymentActionType.PAYMENT_DATE,
                                // https://jirasd.intra.eika.no/browse/NMBP-802
                                // pga. endring fra button til tekstfelt og hvis bruker manuelt oppgir
                                // ugyldig dato, blir "date" undefined.
                                // eslint-disable-next-line
                                // @ts-ignore
                                payload: date,
                            })
                        }
                        onlyBusinessDays
                        warning={errors.paymentDate}
                    />
                </li>
                <li>
                    <NoteField
                        note={state.messageReceiver}
                        placeholder={translated.crossBorderPayment.messageReceiver}
                        labelText={translated.amountNote[NoteTypes.MESSAGE].label}
                        onChange={(event) => {
                            handleChange({ type: CrossBorderPaymentActionType.MESSAGE_RECEIVER, payload: event });
                            setValidationErrors({ messageReceiver: undefined });
                        }}
                        noteMaxLength={MAX_CREDITOR_MESSAGE}
                        error={validationErrors?.messageReceiver}
                        forceDisableKid
                    />
                </li>
                {notDefaultCountryCurrency && (
                    <li>
                        <BicField
                            label={translated.crossBorderPayment.bic}
                            error={validationErrors?.bic}
                            setError={setValidationErrors}
                        />
                    </li>
                )}
                {featureToggles.enableCrossBorderPriority && (
                    <li>
                        <PriorityField
                            label={translated.priorityField.label}
                            priority={state.priority}
                            onChange={(value) => {
                                handleChange({ type: CrossBorderPaymentActionType.SET_PRIORITY, payload: value });
                            }}
                        />
                    </li>
                )}
                {(!state.country?.partOfSepa || showChargeBearer) && (
                    <li>
                        <ChargeBearerField
                            label={translated.chargeBearerField.title}
                            chargeBearer={state.chargeBearer}
                            options={state.country?.chargeBearers}
                            onChange={(value) => {
                                handleChange({
                                    type: CrossBorderPaymentActionType.SET_CHARGE_BEARER,
                                    payload: value,
                                });
                            }}
                        />
                    </li>
                )}
            </SelectList>
            <NavigationButtons onClick={validateAmountFieldsMobile} nextUrl="kategorier" />
        </>
    );
};

export default CrossBorderAmountFields;
